// src/components/CheckoutDashboardTable.jsx
//
// Reusable table for displaying a list of checkout records on the dashboard.
// Each row shows: student name, grade, completion %, and a horizontal grid of
// small status pills — one per section in canonical order.
//
// Sections marked N/A render as small grey dashes; signed sections render as
// green checkmarks; pending sections render as yellow warning dots.
//
// Click a row → onRowClick(record).
//
// Props:
//   records: array of checkout record docs ({ id, studentName, ..., sections })
//   user: current auth user (used to highlight "your" pending sections)
//   showSearchAndFilters: bool — defaults true.
//   emptyMessage: string shown when records is empty
//   onRowClick(record): callback when a row is clicked
//   defaultGradeFilter: string | string[] — initial grade filter value.
//     Use 'ms' for the MS combined filter, 'us' for US combined, or a
//     specific grade like 'Grade 7'. Defaults to 'all'.
//   hideGradeFilter: bool — suppress the grade filter dropdown entirely
//     (used by the MS Checkout page, which is hard-scoped to grades 7-8).
//   hideDeadColumns: bool — auto-hide section columns where every visible
//     record has that section as N/A. Defaults to true. Set false to always
//     show all columns (useful for the Archive view).
//
//     Note: in addition to this data-driven filter, the table ALSO applies
//     a hard catalog filter based on `defaultGradeFilter`. When the scope
//     is 'ms', only sections whose appliesTo includes 'ms' are shown
//     (library, athletics, finance, ms_final). When 'us', only sections
//     that apply to grades 9-12. This catalog filter is always on when
//     scope is set, regardless of hideDeadColumns — protects against
//     stale records leaking irrelevant columns into a scoped view.
//   toolbarExtra: optional render slot after the count label
//   onVisibleRecordsChange: callback(visibleRecords[]) — fires when the
//     visible set changes (e.g. for batch print).

import React, { useMemo, useState } from 'react';
import {
  CHECKOUT_SECTIONS,
  SECTION_STATUS,
  RECORD_STATUS,
  MS_GRADES,
  US_GRADES,
  getRelevantSectionIds,
  computeCompletionPercent,
  canSignOffSection,
  KA_COLORS,
} from '../utils/checkoutHelpers';

const styles = {
  wrapper: { width: '100%' },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'center',
    marginBottom: 14,
    padding: 12,
    background: KA_COLORS.lightGray,
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 6,
  },
  searchInput: {
    flex: 1,
    minWidth: 200,
    padding: '6px 10px',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
    fontSize: 13,
  },
  filterSelect: {
    padding: '6px 10px',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
    fontSize: 13,
    background: '#fff',
    cursor: 'pointer',
  },
  countLabel: { fontSize: 12, color: '#666', fontWeight: 600 },

  tableWrapper: {
    overflowX: 'auto',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 6,
    background: '#fff',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  th: {
    background: KA_COLORS.navy,
    color: '#fff',
    padding: '10px 8px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
  },
  thSection: {
    background: KA_COLORS.navy,
    color: '#fff',
    padding: '10px 4px',
    fontSize: 9,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    textAlign: 'center',
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    minWidth: 22,
    height: 100,
    whiteSpace: 'nowrap',
  },
  tr: (clickable) => ({
    cursor: clickable ? 'pointer' : 'default',
    transition: 'background 0.1s',
    borderBottom: `1px solid ${KA_COLORS.borderGray}`,
  }),
  trHover: { background: '#FFF5F5' },
  td: {
    padding: '8px',
    verticalAlign: 'middle',
    borderRight: `1px solid ${KA_COLORS.borderGray}`,
  },
  studentNameCell: {
    fontWeight: 600,
    color: KA_COLORS.navy,
    fontSize: 13,
  },
  studentMeta: { fontSize: 11, color: '#666' },
  gradePill: (grade) => {
    // Per-grade color so the column is scannable. MS grades get distinct
    // colors from the US palette.
    const colors = {
      'Grade 7':  '#00ACC1', // teal — MS
      'Grade 8':  '#E65100', // coral — MS
      'Grade 9':  '#1976D2',
      'Grade 10': '#00796B',
      'Grade 11': '#F57C00',
      'Grade 12': '#7B1FA2',
    };
    const color = colors[grade] || '#555';
    return {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 10,
      background: color,
      color: '#fff',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      whiteSpace: 'nowrap',
    };
  },
  pctCell: { textAlign: 'center', whiteSpace: 'nowrap' },
  progressMini: {
    width: 60,
    height: 6,
    background: KA_COLORS.borderGray,
    borderRadius: 3,
    overflow: 'hidden',
    margin: '4px auto 0',
  },
  progressMiniFill: (pct, complete) => ({
    width: `${pct}%`,
    height: '100%',
    background: complete ? '#2E7D32' : KA_COLORS.gold,
  }),
  pctLabel: { fontSize: 12, fontWeight: 700, color: KA_COLORS.navy },
  sectionCell: {
    padding: '4px 2px',
    textAlign: 'center',
    borderRight: `1px solid ${KA_COLORS.borderGray}`,
  },
  statusDot: (status, isMine) => {
    const palette = {
      [SECTION_STATUS.SIGNED]: { bg: '#2E7D32', fg: '#fff', char: '✓' },
      [SECTION_STATUS.NA]: { bg: '#E0E0E0', fg: '#888', char: '–' },
      [SECTION_STATUS.PENDING]: { bg: '#FFC107', fg: '#000', char: '·' },
    };
    const p = palette[status] || palette[SECTION_STATUS.PENDING];
    return {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 20,
      height: 20,
      borderRadius: '50%',
      background: p.bg,
      color: p.fg,
      fontSize: 12,
      fontWeight: 700,
      border: isMine ? `2px solid ${KA_COLORS.red}` : '1px solid transparent',
      boxSizing: 'border-box',
    };
  },
  emptyState: {
    padding: 40,
    textAlign: 'center',
    color: '#999',
    fontStyle: 'italic',
    fontSize: 13,
  },
  legend: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
    marginTop: 10,
    fontSize: 11,
    color: '#555',
    alignItems: 'center',
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
};

// -----------------------------------------------------------------------------
// Mini abbreviations for the column headers — keeps them narrow.
// -----------------------------------------------------------------------------
const SECTION_ABBR = {
  scheduling: 'Sched',
  science_books: 'Sci',
  math_books: 'Math',
  history_books: 'Hist',
  world_lang_books: 'WL',
  art_books: 'Art',
  athletics: 'Athl',
  library: 'Lib',
  uco: 'UCO',
  advancement: 'Adv',
  hr_residency: 'HR',
  it: 'IT',
  dorm: 'Dorm',
  osl: 'OSL',
  ms_final: 'MSF',
};

// -----------------------------------------------------------------------------
// Resolve a defaultGradeFilter prop value to a filter state value.
// Accepts: 'all', 'ms', 'us', specific grade string, or array of grades.
// Arrays are recognized as MS if they match MS_GRADES, US if US_GRADES, else
// fall back to 'all' (the array form is mostly a convenience for callers).
// -----------------------------------------------------------------------------
function resolveDefaultGradeFilter(value) {
  if (value == null) return 'all';
  if (Array.isArray(value)) {
    if (
      value.length === MS_GRADES.length &&
      value.every((g) => MS_GRADES.includes(g))
    ) return 'ms';
    if (
      value.length === US_GRADES.length &&
      value.every((g) => US_GRADES.includes(g))
    ) return 'us';
    if (value.length === 1) return value[0];
    return 'all';
  }
  return value;
}

// -----------------------------------------------------------------------------

const SectionDot = ({ section, record, user }) => {
  const data = record.sections?.[section.id];
  const status = data?.status || SECTION_STATUS.PENDING;
  const relevantIds = getRelevantSectionIds(record);
  const isRelevant = relevantIds.includes(section.id);

  if (!isRelevant) {
    return (
      <span style={{ color: '#ccc', fontSize: 12 }} title="Not in this form">
        {' '}·{' '}
      </span>
    );
  }

  const isMyPending =
    status === SECTION_STATUS.PENDING && canSignOffSection(user, section.id);

  const palette = {
    [SECTION_STATUS.SIGNED]: { bg: '#2E7D32', fg: '#fff', char: '✓' },
    [SECTION_STATUS.NA]: { bg: '#E0E0E0', fg: '#888', char: '–' },
    [SECTION_STATUS.PENDING]: { bg: '#FFC107', fg: '#000', char: '·' },
  };
  const p = palette[status] || palette[SECTION_STATUS.PENDING];

  const tooltip = (() => {
    const parts = [section.label, status];
    if (data?.signedByName) parts.push(`by ${data.signedByName}`);
    return parts.join(' — ');
  })();

  return (
    <span style={styles.statusDot(status, isMyPending)} title={tooltip}>
      {p.char}
    </span>
  );
};

// -----------------------------------------------------------------------------

const TableRow = ({ record, user, onRowClick, visibleSections }) => {
  const [hover, setHover] = useState(false);
  const completion = computeCompletionPercent(record);
  const isComplete = completion === 100;

  return (
    <tr
      style={{
        ...styles.tr(!!onRowClick),
        ...(hover ? styles.trHover : {}),
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onRowClick && onRowClick(record)}
    >
      <td style={styles.td}>
        <div style={styles.studentNameCell}>{record.studentName || '—'}</div>
        <div style={styles.studentMeta}>
          {[record.cohort].filter(Boolean).join(' • ')}
        </div>
      </td>

      <td style={styles.td}>
        <span style={styles.gradePill(record.grade)}>
          {record.grade || '—'}
        </span>
      </td>

      <td style={{ ...styles.td, ...styles.pctCell }}>
        <div style={styles.pctLabel}>{completion}%</div>
        <div style={styles.progressMini}>
          <div style={styles.progressMiniFill(completion, isComplete)} />
        </div>
      </td>

      {visibleSections.map((section) => (
        <td key={section.id} style={styles.sectionCell}>
          <SectionDot section={section} record={record} user={user} />
        </td>
      ))}
    </tr>
  );
};

// -----------------------------------------------------------------------------

const CheckoutDashboardTable = ({
  records = [],
  user,
  showSearchAndFilters = true,
  emptyMessage = 'No checkout records to display.',
  onRowClick,
  toolbarExtra,
  onVisibleRecordsChange,
  defaultGradeFilter = 'all',
  hideGradeFilter = false,
  hideDeadColumns = true,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [gradeFilter, setGradeFilter] = useState(() =>
    resolveDefaultGradeFilter(defaultGradeFilter),
  );
  const [sectionFilter, setSectionFilter] = useState('all');
  const [sectionMode, setSectionMode] = useState('pending');
  const [statusFilter, setStatusFilter] = useState('all');

  // Filter records
  const visibleRecords = useMemo(() => {
    let out = records;

    // Grade filter. Special values:
    //   'all' — no filtering
    //   'ms'  — Grades 7-8 combined
    //   'us'  — Grades 9-12 combined
    //   else  — exact grade string match (e.g. 'Grade 10')
    if (gradeFilter !== 'all') {
      if (gradeFilter === 'ms') {
        out = out.filter((r) => MS_GRADES.includes(r.grade));
      } else if (gradeFilter === 'us') {
        out = out.filter((r) => US_GRADES.includes(r.grade));
      } else {
        out = out.filter((r) => r.grade === gradeFilter);
      }
    }

    if (sectionFilter !== 'all') {
      if (sectionMode === 'pending') {
        out = out.filter(
          (r) => r.sections?.[sectionFilter]?.status === SECTION_STATUS.PENDING,
        );
      } else {
        out = out.filter((r) => {
          const status = r.sections?.[sectionFilter]?.status;
          return status && status !== SECTION_STATUS.NA;
        });
      }
    }

    if (statusFilter === 'in_progress') {
      out = out.filter(
        (r) => r.status !== RECORD_STATUS.COMPLETE && computeCompletionPercent(r) < 100,
      );
    } else if (statusFilter === 'complete') {
      out = out.filter(
        (r) => r.status === RECORD_STATUS.COMPLETE || computeCompletionPercent(r) === 100,
      );
    }
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      out = out.filter((r) => {
        const name = (r.studentName || '').toLowerCase();
        const sis = String(r.studentId || '').toLowerCase();
        const grade = (r.grade || '').toLowerCase();
        return name.includes(q) || sis.includes(q) || grade.includes(q);
      });
    }
    return out;
  }, [records, searchTerm, gradeFilter, sectionFilter, sectionMode, statusFilter]);

  // Determine which section columns to display.
  //
  // Two-stage filter:
  //
  // Stage 1 — CATALOG-DRIVEN scope filter (always applied when scope is set).
  //   When defaultGradeFilter is 'ms' or 'us', we restrict CHECKOUT_SECTIONS
  //   to only those whose `appliesTo` includes the corresponding form types.
  //   This is a hard-hide: US-only columns NEVER show in MS scope, regardless
  //   of what's in the record data. Protects against stale records that were
  //   created with a previous catalog and still have lingering 'pending'
  //   statuses on now-irrelevant sections.
  //
  //     'ms' scope → sections where appliesTo includes 'ms'
  //     'us' scope → sections where appliesTo includes any of '9-11', 'senior', 'international'
  //     anything else → all sections
  //
  // Stage 2 — DATA-DRIVEN dead-column filter (only when hideDeadColumns).
  //   On top of the scope filter, hide any column where every visible record
  //   has that section as 'na'. So e.g. in the MS scope, if no MS student
  //   is on the library roster yet, the library column drops out. The
  //   isFinalSignoff sections (osl, ms_final) are exempt from this — they're
  //   always relevant to their scope.
  //
  // Empty record set: skip stage 2 only (otherwise the table looks broken).
  const visibleSections = useMemo(() => {
    // Stage 1: catalog scope filter
    let scoped = CHECKOUT_SECTIONS;
    if (defaultGradeFilter === 'ms') {
      scoped = scoped.filter((s) => s.appliesTo?.includes('ms'));
    } else if (defaultGradeFilter === 'us') {
      scoped = scoped.filter(
        (s) =>
          s.appliesTo?.includes('9-11') ||
          s.appliesTo?.includes('senior') ||
          s.appliesTo?.includes('international'),
      );
    }

    // Stage 2: dead-column filter
    if (!hideDeadColumns || visibleRecords.length === 0) {
      return scoped;
    }
    return scoped.filter((section) => {
      // Final-signoff sections are always shown in their scope
      if (section.isFinalSignoff) return true;
      return visibleRecords.some((r) => {
        const status = r.sections?.[section.id]?.status;
        return status && status !== SECTION_STATUS.NA;
      });
    });
  }, [visibleRecords, hideDeadColumns, defaultGradeFilter]);

  // The section-filter dropdown should only offer sections that actually
  // appear in at least one visible record (otherwise the user can pick a
  // section that produces zero results). Also subject to the same catalog
  // scope filter as visibleSections — no point offering US-only sections
  // in MS scope.
  const sectionFilterOptions = useMemo(() => {
    // Stage 1: catalog scope filter (same logic as visibleSections)
    let scoped = CHECKOUT_SECTIONS;
    if (defaultGradeFilter === 'ms') {
      scoped = scoped.filter((s) => s.appliesTo?.includes('ms'));
    } else if (defaultGradeFilter === 'us') {
      scoped = scoped.filter(
        (s) =>
          s.appliesTo?.includes('9-11') ||
          s.appliesTo?.includes('senior') ||
          s.appliesTo?.includes('international'),
      );
    }

    if (!hideDeadColumns || visibleRecords.length === 0) {
      return scoped;
    }
    // Use the full record set here (pre-section-filter) so the dropdown
    // doesn't shrink as the user narrows down. But we DO scope by grade
    // filter — that's the user's primary axis.
    let scopedRecords = records;
    if (gradeFilter === 'ms') {
      scopedRecords = scopedRecords.filter((r) => MS_GRADES.includes(r.grade));
    } else if (gradeFilter === 'us') {
      scopedRecords = scopedRecords.filter((r) => US_GRADES.includes(r.grade));
    } else if (gradeFilter !== 'all') {
      scopedRecords = scopedRecords.filter((r) => r.grade === gradeFilter);
    }
    if (scopedRecords.length === 0) return scoped;
    return scoped.filter((section) => {
      if (section.isFinalSignoff) return true;
      return scopedRecords.some((r) => {
        const status = r.sections?.[section.id]?.status;
        return status && status !== SECTION_STATUS.NA;
      });
    });
  }, [records, gradeFilter, hideDeadColumns, visibleRecords.length, defaultGradeFilter]);

  // If the currently-selected sectionFilter is no longer in the option list
  // (because the grade filter changed and made it irrelevant), reset it.
  React.useEffect(() => {
    if (sectionFilter === 'all') return;
    if (!sectionFilterOptions.some((s) => s.id === sectionFilter)) {
      setSectionFilter('all');
    }
  }, [sectionFilter, sectionFilterOptions]);

  // Notify parent of the current visible set
  React.useEffect(() => {
    if (typeof onVisibleRecordsChange === 'function') {
      onVisibleRecordsChange(visibleRecords);
    }
  }, [visibleRecords, onVisibleRecordsChange]);

  return (
    <div style={styles.wrapper}>
      {showSearchAndFilters && (
        <div style={styles.controls}>
          <input
            type="text"
            style={styles.searchInput}
            placeholder="Search by name, grade, or SIS ID…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {!hideGradeFilter && (
            <select
              style={styles.filterSelect}
              value={gradeFilter}
              onChange={(e) => setGradeFilter(e.target.value)}
              title="Filter by grade"
            >
              <option value="all">All grades</option>
              <option value="ms">— Middle School (7–8) —</option>
              <option value="Grade 7">Grade 7</option>
              <option value="Grade 8">Grade 8</option>
              <option value="us">— Upper School (9–12) —</option>
              <option value="Grade 9">Grade 9</option>
              <option value="Grade 10">Grade 10</option>
              <option value="Grade 11">Grade 11</option>
              <option value="Grade 12">Grade 12</option>
            </select>
          )}
          <select
            style={styles.filterSelect}
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            title="Filter by section"
          >
            <option value="all">All sections</option>
            {sectionFilterOptions.map((section) => (
              <option key={section.id} value={section.id}>
                {section.label}
              </option>
            ))}
          </select>
          {sectionFilter !== 'all' && (
            <select
              style={styles.filterSelect}
              value={sectionMode}
              onChange={(e) => setSectionMode(e.target.value)}
              title="Section filter mode"
            >
              <option value="pending">Pending only</option>
              <option value="applies">All who need it</option>
            </select>
          )}
          <select
            style={styles.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="in_progress">In progress</option>
            <option value="complete">Complete</option>
          </select>
          <span style={styles.countLabel}>
            {visibleRecords.length} of {records.length}
          </span>
          {toolbarExtra}
        </div>
      )}

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Student</th>
              <th style={styles.th}>Grade</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Progress</th>
              {visibleSections.map((section) => (
                <th key={section.id} style={styles.thSection}>
                  {SECTION_ABBR[section.id] || section.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRecords.length === 0 ? (
              <tr>
                <td
                  colSpan={3 + visibleSections.length}
                  style={styles.emptyState}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              visibleRecords.map((record) => (
                <TableRow
                  key={record.id}
                  record={record}
                  user={user}
                  onRowClick={onRowClick}
                  visibleSections={visibleSections}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.legend}>
        <span style={styles.legendItem}>
          <span style={styles.statusDot(SECTION_STATUS.SIGNED, false)}>✓</span>{' '}
          Signed
        </span>
        <span style={styles.legendItem}>
          <span style={styles.statusDot(SECTION_STATUS.PENDING, false)}>·</span>{' '}
          Pending
        </span>
        <span style={styles.legendItem}>
          <span style={styles.statusDot(SECTION_STATUS.NA, false)}>–</span> N/A
        </span>
        <span style={styles.legendItem}>
          <span style={{ ...styles.statusDot(SECTION_STATUS.PENDING, true) }}>·</span>{' '}
          <span style={{ color: KA_COLORS.red, fontWeight: 600 }}>
            Pending — your section
          </span>
        </span>
      </div>
    </div>
  );
};

export default CheckoutDashboardTable;